import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import useEmblaCarousel from "embla-carousel-react";
import cx from "classnames";
import {useMediaQuery} from "@react-hook/media-query";
import Section from "../../Layout/Section";
import SectionTitle from "../../Layout/Section/Title";
import TestimonialsBackground from "./TestimonialsBackground";
import Media from "../../Common/Media";
import TemplateString from "../../Common/TemplateString";
import Button from "../../UI/NewButton";
import LinkArrow from "../../../assets/svg/link/arrow.svg";
import Quotes from "../../../assets/svg/quotes_2.svg";
import * as styles from "./testimonials.module.css";

const MAIN_OPTIONS = {axis: "y", align: "start"};
const THUMB_OPTIONS = {axis: "y", align: "start"};

const PROGRESS_THUMB_HEIGHT = 32;
const PROGRESS_THUMB_WIDTH = 76;

const Testimonials = ({items}) => {
    const isMobile = useMediaQuery("(max-width: 768px)");

    const [selectedIndex, setSelectedIndex] = useState(0);

    const progressTrackRef = useRef(null);
    const slideRefs = useRef([]);
    const mainViewportRef = useRef(null);

    const [mainRef, mainApi] = useEmblaCarousel(MAIN_OPTIONS);
    const [thumbRef, thumbApi] = useEmblaCarousel(THUMB_OPTIONS);

    const updateProgress = useCallback(
        (index) => {
            const track = progressTrackRef.current;
            if (!track || items.length <= 1) {
                return;
            }

            const trackSize = isMobile ? track.offsetWidth : track.offsetHeight;
            const thumbSize = isMobile ? PROGRESS_THUMB_WIDTH : PROGRESS_THUMB_HEIGHT;
            const maxOffset = Math.max(0, trackSize - thumbSize);

            const progress = index / (items.length - 1);
            const offset = maxOffset * progress;

            const thumb = track.firstElementChild;
            if (!thumb) {
                return;
            }

            thumb.style.transform = isMobile ? `translate3d(${offset}px, 0, 0)` : `translate3d(0, ${offset}px, 0)`;
        },
        [items.length, isMobile],
    );

    const updateSliderHeight = useCallback(() => {
        if (!mainApi || !mainViewportRef.current) {
            return;
        }

        const index = mainApi.selectedScrollSnap();
        const slide = slideRefs.current[index];
        if (!slide) {
            return;
        }

        mainViewportRef.current.style.height = !isMobile ? `${slide.offsetHeight}px` : "auto";
    }, [mainApi, isMobile]);

    const onThumbClick = useCallback(
        (index) => {
            if (!mainApi) {
                return;
            }

            mainApi.scrollTo(index);
        },
        [mainApi],
    );

    const onSelect = useCallback(() => {
        if (!mainApi) {
            return;
        }

        const index = mainApi.selectedScrollSnap();

        setSelectedIndex(index);
        thumbApi?.scrollTo(index);

        requestAnimationFrame(() => {
            updateSliderHeight();
            updateProgress(index);
        });
    }, [mainApi, thumbApi, updateSliderHeight, updateProgress]);

    useEffect(() => {
        if (!mainApi) {
            return () => {};
        }

        onSelect();
        mainApi.on("select", onSelect);
        mainApi.on("reInit", onSelect);

        return () => {
            mainApi.off("select", onSelect);
            mainApi.off("reInit", onSelect);
        };
    }, [mainApi, onSelect]);

    useEffect(() => {
        if (!mainApi) {
            return () => {};
        }

        const observer = new ResizeObserver(() => {
            updateSliderHeight();
        });

        slideRefs.current.forEach((slide) => {
            if (slide) {
                observer.observe(slide);
            }
        });

        return () => {
            observer.disconnect();
        };
    }, [mainApi, updateSliderHeight]);

    useEffect(() => {
        if (!thumbApi) {
            return () => {};
        }

        const reInit = () => {
            requestAnimationFrame(() => {
                thumbApi.reInit();
            });
        };

        if (document.readyState === "complete") {
            reInit();
        } else {
            window.addEventListener("load", reInit);
        }

        return () => {
            window.removeEventListener("load", reInit);
        };
    }, [thumbApi]);

    return (
        <Section>
            <div className={styles.testimonials}>
                <div className={styles.testimonials__title}>
                    <SectionTitle>Что говорят наши клиенты</SectionTitle>
                </div>
                <div className={styles.testimonials__panel}>
                    <div className={styles.testimonials__logos}>
                        <div ref={progressTrackRef} className={styles.testimonials__logosProgress}>
                            <div className={styles.testimonials__logosProgressThumb} />
                        </div>

                        <div ref={thumbRef} className={styles.testimonials__logosViewport}>
                            <div className={styles.testimonials__logosContainer}>
                                {items.map(({id, logo}, idx) => (
                                    <div
                                        key={id}
                                        className={cx(styles.testimonials__logosSlide, {
                                            [styles.testimonials__logosSlide_selected]: idx === selectedIndex,
                                        })}
                                    >
                                        <div className={styles.testimonials__logoWrapper}>
                                            <Media
                                                media={{content: logo}}
                                                className={styles.testimonials__logo}
                                                imageProps={{imgClassName: styles.testimonials__logoImage}}
                                                plainImage
                                            />
                                            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                                            <button
                                                type="button"
                                                className={styles.testimonials__logoButton}
                                                onClick={() => onThumbClick(idx)}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div
                        className={styles.testimonials__slider}
                        ref={(node) => {
                            mainRef(node);
                            mainViewportRef.current = node;
                        }}
                    >
                        <div className={styles.testimonials__sliderContainer}>
                            {items.map(({id, description, content, authorPhoto, author, authorPosition, url}, idx) => (
                                <div
                                    ref={(node) => {
                                        slideRefs.current[idx] = node;
                                    }}
                                    key={id}
                                    className={styles.testimonials__item}
                                >
                                    <div className={styles.testimonials__itemQuoteWrapper}>
                                        <Quotes />
                                        <blockquote className={styles.testimonials__itemQuote}>
                                            <TemplateString template={description} />
                                        </blockquote>
                                    </div>
                                    <p className={styles.testimonials__itemContent}>
                                        <TemplateString template={content} />
                                    </p>
                                    <div className={styles.testimonials__itemFooter}>
                                        <div className={styles.testimonials__itemAuthor}>
                                            <Media
                                                media={{content: authorPhoto}}
                                                className={styles.testimonials__itemAuthorPhoto}
                                            />
                                            <div>
                                                <p className={styles.testimonials__itemAuthorName}>{author}</p>
                                                <p className={styles.testimonials__itemAuthorPosition}>
                                                    {authorPosition}
                                                </p>
                                            </div>
                                        </div>
                                        <Button href={url} isLink className={styles.testimonials__itemButton}>
                                            Читать подробнее <LinkArrow />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <TestimonialsBackground />
                </div>
            </div>
        </Section>
    );
};

export default Testimonials;
